'use client';

import { c as _c } from "react/compiler-runtime";
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { Page } from '@components/page';
import { getPaginationUlStylesAsClassName, getPaginationLiStylesAsClassName } from './pagination-styles';
interface PageLinkProps {
  page?: number;
  children: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  withIcon?: boolean;
  ariaLabel?: string;
  generatePageUrl?: (page: number) => string;
  onPageClick?: (page: number) => void;
}
const PageLink = t0 => {
  const $ = _c(21);
  const {
    page,
    children,
    selected: t1,
    disabled: t2,
    withIcon: t3,
    ariaLabel,
    generatePageUrl,
    onPageClick
  } = t0;
  const selected = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const withIcon = t3 === undefined ? false : t3;
  if (disabled || !page || selected) {
    const t4 = !disabled;
    let t5;
    if ($[0] !== ariaLabel || $[1] !== children || $[2] !== disabled || $[3] !== selected || $[4] !== t4 || $[5] !== withIcon) {
      t5 = <Page selected={selected} disabled={disabled} selectable={t4} withIcon={withIcon} ariaLabel={ariaLabel}>{children}</Page>;
      $[0] = ariaLabel;
      $[1] = children;
      $[2] = disabled;
      $[3] = selected;
      $[4] = t4;
      $[5] = withIcon;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  let t4;
  if ($[7] !== generatePageUrl || $[8] !== page) {
    t4 = generatePageUrl(page);
    $[7] = generatePageUrl;
    $[8] = page;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onPageClick || $[11] !== page) {
    t5 = () => onPageClick?.(page);
    $[10] = onPageClick;
    $[11] = page;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== children || $[14] !== withIcon) {
    t6 = <Page selected={false} disabled={false} selectable={true} withIcon={withIcon}>{children}</Page>;
    $[13] = children;
    $[14] = withIcon;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== ariaLabel || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = <Link to={t4} aria-label={ariaLabel} onClick={t5} className="no-underline">{t6}</Link>;
    $[16] = ariaLabel;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
export interface ILinkPaginationProps {
  /** The total number of pages */
  totalPages: number;
  /** Current page to start */
  currentPage?: number;
  /** Total number of pages the Pagination will show in screen */
  boundaryPagesToShow?: number;
  /** Number of pages to the left and right of the current page */
  siblingCount?: number;
  /** Aria label for the nav element */
  navAriaLabel?: string;
  /** Aria label for all pages, should be something like "Go to page" as the component will add the page number after the text */
  pagesAriaLabel?: string;
  /** Function to generate URLs for each page */
  generatePageUrl: (page: number) => string;
  /** Optional function to be executed when the page changes (for analytics, etc.) */
  onPageChange?: (page: number) => void;
}
export const LinkPagination = t0 => {
  const $ = _c(72);
  const {
    totalPages,
    currentPage,
    boundaryPagesToShow: t1,
    siblingCount: t2,
    navAriaLabel: t3,
    pagesAriaLabel: t4,
    generatePageUrl,
    onPageChange
  } = t0;
  const boundaryPagesToShow = t1 === undefined ? 7 : t1;
  const siblingCount = t2 === undefined ? 2 : t2;
  const navAriaLabel = t3 === undefined ? "Pagination" : t3;
  const pagesAriaLabel = t4 === undefined ? "Go to page" : t4;
  const dotsAllowed = totalPages - boundaryPagesToShow >= siblingCount;
  let t5;
  if ($[0] !== totalPages) {
    t5 = Array.from(Array(totalPages).keys()).map(_temp);
    $[0] = totalPages;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const pages = t5;
  const selectedPage = currentPage ?? pages[0] ?? 0;
  let t6;
  if ($[2] !== onPageChange) {
    t6 = page_0 => {
      onPageChange?.(page_0);
    };
    $[2] = onPageChange;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const handlePageClick = t6;
  let t7;
  if ($[4] !== boundaryPagesToShow || $[5] !== selectedPage || $[6] !== siblingCount) {
    t7 = page_1 => {
      const firstPagesBeforeDots = boundaryPagesToShow - siblingCount;
      return selectedPage <= firstPagesBeforeDots && page_1 <= boundaryPagesToShow;
    };
    $[4] = boundaryPagesToShow;
    $[5] = selectedPage;
    $[6] = siblingCount;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const isPageBeforeDots = t7;
  let t8;
  if ($[8] !== boundaryPagesToShow || $[9] !== selectedPage || $[10] !== siblingCount || $[11] !== totalPages) {
    t8 = page_2 => {
      const lastPages = totalPages - boundaryPagesToShow;
      const lastPagesAfterDots = lastPages + siblingCount;
      return selectedPage > lastPagesAfterDots && page_2 > lastPages;
    };
    $[8] = boundaryPagesToShow;
    $[9] = selectedPage;
    $[10] = siblingCount;
    $[11] = totalPages;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const isPageAfterDots = t8;
  let t9;
  if ($[13] !== isPageAfterDots || $[14] !== isPageBeforeDots || $[15] !== selectedPage || $[16] !== siblingCount || $[17] !== totalPages) {
    t9 = page_3 => {
      const isFirstPage = page_3 === 1;
      const isLastPage = page_3 === totalPages;
      const inPagesBefore = page_3 >= selectedPage - siblingCount;
      const inPagesAfter = page_3 <= selectedPage + siblingCount;
      const inMiddlePages = inPagesBefore && inPagesAfter && !isPageBeforeDots(selectedPage) && !isPageAfterDots(selectedPage);
      return !isFirstPage && !isLastPage && (isPageBeforeDots(page_3) || isPageAfterDots(page_3) || inMiddlePages);
    };
    $[13] = isPageAfterDots;
    $[14] = isPageBeforeDots;
    $[15] = selectedPage;
    $[16] = siblingCount;
    $[17] = totalPages;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const canRenderPage = t9;
  let t10;
  if ($[19] !== canRenderPage || $[20] !== pages) {
    t10 = pages.filter(canRenderPage);
    $[19] = canRenderPage;
    $[20] = pages;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const pagesToRender = t10;
  const t11 = selectedPage > 1 ? selectedPage - 1 : undefined;
  const t12 = selectedPage === 1;
  const t13 = selectedPage > 1 ? `${pagesAriaLabel} ${selectedPage - 1}` : undefined;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon className="text-primary-main" name={IconNames.CHEVRON_LEFT} />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== generatePageUrl || $[24] !== handlePageClick || $[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t15 = <li key="prev-page" className={getPaginationLiStylesAsClassName}><PageLink page={t11} withIcon={true} disabled={t12} ariaLabel={t13} generatePageUrl={generatePageUrl} onPageClick={handlePageClick}>{t14}</PageLink></li>;
    $[23] = generatePageUrl;
    $[24] = handlePageClick;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = selectedPage === 1;
  const t17 = selectedPage !== 1 ? `${pagesAriaLabel} 1` : undefined;
  let t18;
  if ($[29] !== generatePageUrl || $[30] !== handlePageClick || $[31] !== t16 || $[32] !== t17) {
    t18 = <li key="page-1" className={getPaginationLiStylesAsClassName}><PageLink page={1} selected={t16} ariaLabel={t17} generatePageUrl={generatePageUrl} onPageClick={handlePageClick}>1</PageLink></li>;
    $[29] = generatePageUrl;
    $[30] = handlePageClick;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== dotsAllowed || $[35] !== isPageBeforeDots || $[36] !== selectedPage) {
    t19 = !isPageBeforeDots(selectedPage) && dotsAllowed && <li key="page-dots-1" className={getPaginationLiStylesAsClassName}><PageLink disabled={true}>...</PageLink></li>;
    $[34] = dotsAllowed;
    $[35] = isPageBeforeDots;
    $[36] = selectedPage;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== generatePageUrl || $[39] !== handlePageClick || $[40] !== pagesAriaLabel || $[41] !== pagesToRender || $[42] !== selectedPage) {
    t20 = pagesToRender.map(page_4 => <li key={`page-${page_4}`} className={getPaginationLiStylesAsClassName}><PageLink page={page_4} selected={selectedPage === page_4} ariaLabel={page_4 !== selectedPage ? `${pagesAriaLabel} ${page_4}` : undefined} generatePageUrl={generatePageUrl} onPageClick={handlePageClick}>{page_4}</PageLink></li>);
    $[38] = generatePageUrl;
    $[39] = handlePageClick;
    $[40] = pagesAriaLabel;
    $[41] = pagesToRender;
    $[42] = selectedPage;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== dotsAllowed || $[45] !== isPageAfterDots || $[46] !== selectedPage) {
    t21 = !isPageAfterDots(selectedPage) && dotsAllowed && <li key="page-dots-2" className={getPaginationLiStylesAsClassName}><PageLink disabled={true}>...</PageLink></li>;
    $[44] = dotsAllowed;
    $[45] = isPageAfterDots;
    $[46] = selectedPage;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== generatePageUrl || $[49] !== handlePageClick || $[50] !== pagesAriaLabel || $[51] !== selectedPage || $[52] !== totalPages) {
    t22 = totalPages > 1 && <li key={`page-${totalPages}`} className={getPaginationLiStylesAsClassName}><PageLink page={totalPages} selected={selectedPage === totalPages} ariaLabel={selectedPage !== totalPages ? `${pagesAriaLabel} ${totalPages}` : undefined} generatePageUrl={generatePageUrl} onPageClick={handlePageClick}>{totalPages}</PageLink></li>;
    $[48] = generatePageUrl;
    $[49] = handlePageClick;
    $[50] = pagesAriaLabel;
    $[51] = selectedPage;
    $[52] = totalPages;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const t23 = selectedPage < totalPages ? selectedPage + 1 : undefined;
  const t24 = totalPages === selectedPage;
  const t25 = selectedPage < totalPages ? `${pagesAriaLabel} ${selectedPage + 1}` : undefined;
  let t26;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Icon className="text-primary-main" name={IconNames.CHEVRON_RIGHT} />;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== generatePageUrl || $[56] !== handlePageClick || $[57] !== t23 || $[58] !== t24 || $[59] !== t25) {
    t27 = <li key="next-page" className={getPaginationLiStylesAsClassName}><PageLink page={t23} withIcon={true} disabled={t24} ariaLabel={t25} generatePageUrl={generatePageUrl} onPageClick={handlePageClick}>{t26}</PageLink></li>;
    $[55] = generatePageUrl;
    $[56] = handlePageClick;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t15 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21 || $[66] !== t22 || $[67] !== t27) {
    t28 = <ol className={getPaginationUlStylesAsClassName + " space-x-1"}>{t15}{t18}{t19}{t20}{t21}{t22}{t27}</ol>;
    $[61] = t15;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== navAriaLabel || $[70] !== t28) {
    t29 = <nav aria-label={navAriaLabel}>{t28}</nav>;
    $[69] = navAriaLabel;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  return t29;
};
function _temp(page) {
  return page + 1;
}